import { useState, useEffect } from 'react';
import { Table } from '@consta/uikit/Table';
import { Pagination } from '@consta/uikit/Pagination'; // Добавляем импорт
import { Text } from '@consta/uikit/Text';
import { Button } from '@consta/uikit/Button';
import { IconEdit } from '@consta/icons/IconEdit';
import classes from './style/ProjectWells.module.scss';
import { Project } from '../../../../../services/types/TProjectServices';
import { useLazyGetWellByProjectQuery } from '../../../../../services/wellService';

interface ProjectWellsProps {
  projectData?: Project;
  projectUuid?: string;
}

interface RowWells {
  name: string;
  cipher: string;
  brigade: string;
  horizont: string;
  // horizonCode: string;
  plast: string;
  liquid: string;
  water: string;
  category: string;
  areas: string;
  oil_rate: string;
  water_rate: string;
  x_coords: string;
  y_coords: string;
  studied_phase: string;
  research_objective: string;
  note: string;
  sample: string;
  sampling_date: string;
  sample_note: string;
  vN: string;
  razlitN: string;
  is_water_sample_received: string;
  vW: string;
  razlitW: string;
  density: string;
  uep: string;
  is_gh: string;
  is_icp: string;
  is_hav: string;
  is_kapel: string;
  action?: any;
  rowNumber?: number; // Добавляем поле для номера строки
}

const ProjectWells = ({ projectData, projectUuid }: ProjectWellsProps) => {
  const [getWellsByProject, { data: wellsData, isLoading: isLoadingWells }] = useLazyGetWellByProjectQuery();
  
  // Состояния для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    if (projectUuid) {
      getWellsByProject({
        project_uuid: projectUuid,
        limit: 9999 // Загружаем все данные, пагинацию делаем на клиенте
      });
      // Сбрасываем страницу при смене проекта
      setCurrentPage(1);
    }
  }, [projectUuid, getWellsByProject]);

  // Сбрасываем страницу при изменении данных
  useEffect(() => {
    setCurrentPage(1);
  }, [wellsData?.results?.length]);

  // Функции для пагинации
  const getPaginatedData = (data: any[], page: number) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const addIndexToData = (data: any[], page: number) => {
    return data.map((item, idx) => ({
      ...item,
      rowNumber: (page - 1) * itemsPerPage + idx + 1
    }));
  };

  // Преобразование данных из API в формат таблицы
  const transformWellData = (well: any): RowWells => {
    // Форматируем горизонты в строку
    const horizonsText = well.horizons_info && Array.isArray(well.horizons_info) 
      ? well.horizons_info.map((h: any) => h.name).join(', ')
      : '-';
    const horizonsCodeText = well.horizons_info && Array.isArray(well.horizons_info) && well.horizons_info?.filter((item) => item.code)?.length > 1
      ? well.horizons_info.map((h: any) => h.code).join(', ')
      : '-';

    // Форматируем пласты в строку
    const layersText = well.layers_info && Array.isArray(well.layers_info) 
      ? well.layers_info.map((l: any) => l.name).join(', ')
      : '-';

    return {
      name: well.number?.toString() || '-',
      cipher: well.workshop?.toString() || '-',
      brigade: well.team?.toString() || '-',
      horizont: horizonsText,
      // horizonCode: horizonsCodeText,
      plast: layersText,
      liquid: well.liquid_rate?.toString() || '-',
      water: well.water_cut?.toString() || '-',
      category: well.category_name || well.category || '-',
      areas: well.area_name || '-',
      oil_rate: well.oil_rate?.toString() || '-',
      water_rate: well.water_rate?.toString() || '-',
      x_coords: well.x_coords?.toString() || '-',
      y_coords: well.y_coords?.toString() || '-',
      studied_phase: well.studied_phase || '-',
      research_objective: well.research_objective || '-',
      note: well.note || '-',
      sample: well.is_sample_taken ? 'Да' : 'Нет',
      sampling_date: well.sampling_date || '-',
      sample_note: well.sample_note || '-',
      vN: well.oil_volume?.toString() || '-',
      razlitN: well.is_oil_split ? 'Да' : 'Нет',
      is_water_sample_received: well.is_water_sample_received ? 'Да' : 'Нет',
      vW: well.water_volume?.toString() || '-',
      razlitW: well.is_water_split ? 'Да' : 'Нет',
      density: well.water_density?.toString() || '-',
      uep: well.water_ec?.toString() || '-',
      is_gh: well.is_gh ? 'Да' : 'Нет',
      is_icp: well.is_icp ? 'Да' : 'Нет',
      is_hav: well.is_hav ? 'Да' : 'Нет',
      is_kapel: well.is_kapel ? 'Да' : 'Нет',
    };
  };

  // Правильное преобразование данных из API
  const allRows: RowWells[] = wellsData?.results 
    ? wellsData.results.map(transformWellData)
    : [];

  // Получаем пагинированные данные
  const paginatedRows = addIndexToData(getPaginatedData(allRows, currentPage), currentPage);
  const totalPages = Math.ceil(allRows.length / itemsPerPage);

  const columnsWells = [
    { 
      title: '№', 
      accessor: 'rowNumber',
      width: 60,
      renderCell: (row: RowWells) => (
        <Text weight="bold">{row.rowNumber}</Text>
      )
    },
    { title: '№ скважины', accessor: 'name', sortable: true, width: 150 },
    { title: 'Цех', accessor: 'cipher', sortable: true, width: 120 },
    { title: 'Бригада', accessor: 'brigade', sortable: true, width: 120 },
    { title: 'Название горизонта', accessor: 'horizont', sortable: true, width: 170 },
    // { title: 'Шифр горизонта', accessor: 'horizonCode', sortable: true, width: 170 },
    { title: 'Пласт', accessor: 'plast', sortable: true, width: 200 },
    { title: 'Дебит скважины по жидкости (м³/сут)', accessor: 'liquid', sortable: true, width: 210 },
    { title: 'Обводнённость (%)', accessor: 'water', sortable: true, width: 190 },
    { title: 'Категория', accessor: 'category', sortable: true, width: 140 },
    { title: 'Участок', accessor: 'areas', sortable: true, width: 170 },
    { title: 'Q нефти (т/сут)', accessor: 'oil_rate', sortable: true, width: 170 },
    { title: 'Q жидкости (т/сут)', accessor: 'water_rate', sortable: true, width: 170 },
    { title: 'Координаты X', accessor: 'x_coords', sortable: true, width: 150 },
    { title: 'Координаты Y', accessor: 'y_coords', sortable: true, width: 150 },
    { title: 'Исследуемая фаза', accessor: 'studied_phase', sortable: true, width: 190 },
    { title: 'Цель исследования', accessor: 'research_objective', sortable: true, width: 190 },
    { title: 'Примечание', accessor: 'note', sortable: true, width: 170 },
    { title: 'Проба отобрана', accessor: 'sample', sortable: true, width: 160 },
    { title: 'Дата отбора пробы', accessor: 'sampling_date', sortable: true, width: 180 },
    { title: 'Примечание пробы', accessor: 'sample_note', sortable: true, width: 160 },
    { title: 'Объем нефти', accessor: 'vN', sortable: true, width: 150 },
    { title: 'Разлито нефть', accessor: 'razlitN', sortable: true, width: 150 },
    { title: 'Получен образец воды', accessor: 'is_water_sample_received', sortable: true, width: 170 },
    { title: 'Объем воды', accessor: 'vW', sortable: true, width: 150 },
    { title: 'Отфильтровано вода', accessor: 'razlitW', sortable: true, width: 150 },
    { title: 'Плотность', accessor: 'density', sortable: true, width: 120 },
    { title: 'УЭП', accessor: 'uep', sortable: true, width: 120 },
    { title: 'ГХ', accessor: 'is_gh', sortable: true, width: 80 },
    { title: 'ICP', accessor: 'is_icp', sortable: true, width: 80 },
    { title: 'ХАВ', accessor: 'is_hav', sortable: true, width: 80 },
    { title: 'Капель', accessor: 'is_kapel', sortable: true, width: 120 },
  ];

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    // Прокручиваем таблицу вверх при смене страницы
    const tableElement = document.querySelector(`.${classes.table}`);
    if (tableElement) {
      tableElement.scrollTop = 0;
    }
  };

  return (
    <div className={classes.main}>
      <div className={classes.header}>
        <Text size='2xl' weight='bold'>
          Скважины проекта {projectData?.number ? `№ ${projectData.number}` : projectUuid}
        </Text>
        {isLoadingWells && <Text size='s' view='secondary'>Загрузка данных...</Text>}
        {!isLoadingWells && wellsData && (
          <Text size='s' view='secondary'>
            Показано: {paginatedRows.length} из {wellsData.count || 0} скважин
          </Text>
        )}
      </div>
      
      <div className={classes.table}>
        <Table
          rows={paginatedRows} // Используем пагинированные данные
          columns={columnsWells} 
          stickyHeader
          stickyColumns={1 && 2}
          borderBetweenRows 
          borderBetweenColumns
          emptyRowsPlaceholder={
            <Text>
              {isLoadingWells ? 'Загрузка данных...' : 'Нет данных о скважинах'}
            </Text>
          }
          verticalAlign="top"
        />
        
        {totalPages > 1 && (
          <div className={classes.paginationSection} style={{ 
            marginTop: '20px', 
            marginBottom: '20px',
            display: 'flex', 
            justifyContent: 'center' 
          }}>
            <Pagination
              items={totalPages}
              value={currentPage}
              onChange={handlePageChange}
              showFirstPage
              showLastPage
              visibleCount={7}
              size='s'
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectWells;